import type { JSX } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';

export type TooltipSide = 'top' | 'right' | 'bottom' | 'left';

const SHOW_DELAY_MS = 360;
const GAP_PX = 8;
const VIEW_PAD_PX = 8;

type TipBox = { left: number; top: number };

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function placeTip(trigger: DOMRect, tip: DOMRect, side: TooltipSide): TipBox {
  let left = 0;
  let top = 0;

  switch (side) {
    case 'right':
      left = trigger.right + GAP_PX;
      top = trigger.top + trigger.height / 2 - tip.height / 2;
      break;
    case 'left':
      left = trigger.left - GAP_PX - tip.width;
      top = trigger.top + trigger.height / 2 - tip.height / 2;
      break;
    case 'top':
      left = trigger.left + trigger.width / 2 - tip.width / 2;
      top = trigger.top - GAP_PX - tip.height;
      break;
    case 'bottom':
      left = trigger.left + trigger.width / 2 - tip.width / 2;
      top = trigger.bottom + GAP_PX;
      break;
  }

  const maxLeft = window.innerWidth - tip.width - VIEW_PAD_PX;
  const maxTop = window.innerHeight - tip.height - VIEW_PAD_PX;

  return {
    left: clamp(left, VIEW_PAD_PX, Math.max(VIEW_PAD_PX, maxLeft)),
    top: clamp(top, VIEW_PAD_PX, Math.max(VIEW_PAD_PX, maxTop)),
  };
}

function triggerFromHost(host: HTMLElement): HTMLElement {
  return (host.firstElementChild as HTMLElement | null) ?? host;
}

/**
 * Chrome tooltip with a caret. Portaled to `#ui-root` so overflow-hidden rails and strips
 * cannot clip it. Do not also set `title` — that is the native tooltip this replaces.
 */
export function Tooltip({
  label,
  shortcut,
  side = 'bottom',
  disabled = false,
  children,
}: {
  label: string;
  shortcut?: string;
  side?: TooltipSide;
  disabled?: boolean;
  children: JSX.Element;
}): JSX.Element {
  const hostRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState<TipBox | null>(null);

  const hide = (): void => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setOpen(false);
    setBox(null);
  };

  const show = (): void => {
    if (disabled) {
      return;
    }

    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setOpen(true);
    }, SHOW_DELAY_MS);
  };

  useEffect(() => {
    if (disabled) {
      hide();
    }
  }, [disabled]);

  useEffect(() => {
    const host = hostRef.current;
    const trigger = host ? triggerFromHost(host) : null;

    if (!trigger) {
      return;
    }

    const onEnter = (): void => {
      show();
    };
    const onLeave = (): void => {
      hide();
    };

    trigger.addEventListener('mouseenter', onEnter);
    trigger.addEventListener('mouseleave', onLeave);
    trigger.addEventListener('focus', onEnter);
    trigger.addEventListener('blur', onLeave);

    return () => {
      trigger.removeEventListener('mouseenter', onEnter);
      trigger.removeEventListener('mouseleave', onLeave);
      trigger.removeEventListener('focus', onEnter);
      trigger.removeEventListener('blur', onLeave);
      hide();
    };
  }, [label, shortcut, side]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const host = hostRef.current;
    const tip = tipRef.current;

    if (!host || !tip) {
      return;
    }

    const layout = (): void => {
      setBox(placeTip(triggerFromHost(host).getBoundingClientRect(), tip.getBoundingClientRect(), side));
    };

    layout();
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    window.addEventListener('pointerdown', hide, true);

    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      window.removeEventListener('pointerdown', hide, true);
    };
  }, [open, side, label, shortcut]);

  const root = typeof document === 'undefined' ? null : document.getElementById('ui-root') ?? document.body;

  return (
    <>
      <span ref={hostRef} class="ui-tooltip-host">
        {children}
      </span>
      {open && root
        ? createPortal(
            <div
              ref={tipRef}
              class="ui-tooltip"
              data-side={side}
              role="tooltip"
              style={
                box
                  ? { left: `${box.left}px`, top: `${box.top}px` }
                  : { left: 0, top: 0, visibility: 'hidden' }
              }
            >
              <span class="ui-tooltip-label">{label}</span>
              {shortcut ? <kbd class="ui-tooltip-key">{shortcut}</kbd> : null}
              <span class="ui-tooltip-caret" aria-hidden="true" />
            </div>,
            root,
          )
        : null}
    </>
  );
}
